import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../constants";

/**
 * CalendarIntegration — provider connect/disconnect & sync controls.
 */
export default function CalendarIntegration() {
  const [calPrefs, setCalPrefs] = useState(null);
  const [calStatus, setCalStatus] = useState(null);
  const [appleId, setAppleId] = useState("");
  const [applePassword, setApplePassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statusRes, prefsRes] = await Promise.all([
        axios.get(`${API}/calendar/status`),
        axios.get(`${API}/calendar/preferences/admin`),
      ]);
      setCalStatus(statusRes.data);
      setCalPrefs(prefsRes.data);
    } catch { /* not configured yet */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async (provider) => {
    if (provider === "google") window.location.href = `${API}/calendar/google/auth/admin`;
    else if (provider === "outlook") window.location.href = `${API}/calendar/outlook/auth/admin`;
    else if (provider === "apple") {
      if (!appleId || !applePassword) { toast.error("Enter Apple ID and app-specific password"); return; }
      try {
        await axios.post(`${API}/calendar/apple/connect/admin`, { apple_id: appleId, app_password: applePassword });
        toast.success("Apple Calendar connected");
        setAppleId(""); setApplePassword(""); load();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to connect"); }
    }
  };

  const disconnect = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect/admin`);
      toast.success("Calendar disconnected");
      load();
    } catch { toast.error("Failed to disconnect"); }
  };

  const sync = async (direction) => {
    try {
      setIsLoading(true);
      const res = await axios.post(`${API}/calendar/sync/${direction}/admin`);
      toast.success(`${direction === "push" ? "Pushed" : "Imported"} ${res.data.synced || res.data.imported || 0} events`);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setIsLoading(false); }
  };

  return (
    <div data-testid="crm-cal-integration">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connection Status */}
        <div className="card-glass p-6" data-testid="cal-status-card">
          <h2 className="font-heading text-xl font-bold text-white mb-4">Calendar Connection</h2>
          {calPrefs?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-[#A3E635]/10 border border-[#A3E635]/20">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: calPrefs.provider === "google" ? "#4285F420" : calPrefs.provider === "outlook" ? "#0078D420" : "#55555520" }}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={calPrefs.provider === "google" ? "#4285F4" : calPrefs.provider === "outlook" ? "#0078D4" : "#888"} strokeWidth="2"><path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                </div>
                <div>
                  <p className="text-white font-semibold capitalize">{calPrefs.provider} Calendar</p>
                  <p className="text-[#A3E635] text-sm">Connected{calPrefs.email ? ` (${calPrefs.email})` : ""}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => sync("push")} className="btn-primary flex-1 text-sm" data-testid="cal-sync-push" disabled={isLoading}>Push CRM Events</button>
                <button onClick={() => sync("pull")} className="btn-secondary flex-1 text-sm" data-testid="cal-sync-pull" disabled={isLoading}>Pull External Events</button>
              </div>
              {calPrefs.last_sync && <p className="text-[#E8E4DC] text-xs">Last synced: {new Date(calPrefs.last_sync).toLocaleString()}</p>}
              <button onClick={disconnect} className="text-red-400 hover:text-red-300 text-sm underline" data-testid="cal-disconnect">Disconnect Calendar</button>
            </div>
          ) : (
            <p className="text-[#E8E4DC] text-sm mb-4">No calendar connected. Choose a provider below to sync your CRM calendar.</p>
          )}
        </div>

        {/* Provider Selection */}
        <div className="space-y-4">
          <div className="card-glass p-5" data-testid="cal-google-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#4285F415" }}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#4285F4"><path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm5.5 16.5h-11V7.5h11v9z" /></svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Google Calendar</p>
                  <p className="text-[#E8E4DC] text-xs">{calStatus?.google?.configured ? "Ready to connect" : "Requires admin config"}</p>
                </div>
              </div>
              <button onClick={() => connect("google")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calStatus?.google?.configured ? "bg-[#4285F4] text-white hover:bg-[#3367D6]" : "bg-white/5 text-[#E8E4DC] cursor-not-allowed"}`} disabled={!calStatus?.google?.configured} data-testid="cal-connect-google">
                {calPrefs?.provider === "google" && calPrefs?.connected ? "Connected" : "Connect"}
              </button>
            </div>
          </div>

          <div className="card-glass p-5" data-testid="cal-outlook-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#0078D415" }}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0078D4"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 10h8M8 14h5" stroke="white" strokeWidth="1.5" /></svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Outlook Calendar</p>
                  <p className="text-[#E8E4DC] text-xs">{calStatus?.outlook?.configured ? "Ready to connect" : "Requires admin config"}</p>
                </div>
              </div>
              <button onClick={() => connect("outlook")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calStatus?.outlook?.configured ? "bg-[#0078D4] text-white hover:bg-[#005A9E]" : "bg-white/5 text-[#E8E4DC] cursor-not-allowed"}`} disabled={!calStatus?.outlook?.configured} data-testid="cal-connect-outlook">
                {calPrefs?.provider === "outlook" && calPrefs?.connected ? "Connected" : "Connect"}
              </button>
            </div>
          </div>

          <div className="card-glass p-5" data-testid="cal-apple-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#55555515" }}>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#999"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83" /></svg>
              </div>
              <div>
                <p className="text-white font-semibold">Apple Calendar</p>
                <p className="text-[#E8E4DC] text-xs">Connect via Apple ID + App-Specific Password</p>
              </div>
            </div>
            {calPrefs?.provider === "apple" && calPrefs?.connected ? (
              <p className="text-[#A3E635] text-sm">Connected ({calPrefs.email})</p>
            ) : (
              <div className="space-y-2">
                <input type="email" value={appleId} onChange={(e) => setAppleId(e.target.value)} className="input-dark w-full text-sm" placeholder="Apple ID" data-testid="cal-apple-id" />
                <input type="password" value={applePassword} onChange={(e) => setApplePassword(e.target.value)} className="input-dark w-full text-sm" placeholder="App-Specific Password" data-testid="cal-apple-password" />
                <button onClick={() => connect("apple")} className="btn-primary w-full text-sm" data-testid="cal-connect-apple">Connect Apple Calendar</button>
                <p className="text-[#E8E4DC] text-xs">Generate an app-specific password at appleid.apple.com</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
